import { useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '../components/shared/PaginationBar';

/**
 * usePagination — client-side paging over an already-fully-loaded array.
 *
 * Every list module in this app (Clients, Cases, Documents, Tasks, Invoices,
 * ...) receives its complete filtered/sorted dataset in memory already —
 * see App.js's `createFetchFunction`, which walks every backend page up
 * front — so pagination here is just slicing that array for display, not a
 * new fetch. Returns `pageItems` (the current page's slice) and
 * `paginationProps`, the exact prop shape `<PaginationBar>` expects.
 *
 * `page`/`pageSize` are intentionally *not* reset when `items` itself
 * changes identity (e.g. a new search term produces a shorter array) —
 * `currentPage` is clamped to the new `totalPages` automatically, so a
 * stale `page` value just renders the last valid page instead of an empty
 * one, without needing an effect.
 *
 * Usage:
 *   const { pageItems, paginationProps } = usePagination(filteredClients);
 *   ...
 *   {pageItems.map(client => ...)}
 *   ...
 *   <PaginationBar {...paginationProps} emptyLabel="No clients found." />
 */
export function usePagination(items, defaultPageSize = PAGE_SIZE_OPTIONS[0]) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageItems = items.slice(pageStart, pageStart + pageSize);

  return {
    pageItems,
    paginationProps: {
      totalCount,
      pageStart,
      pageSize,
      currentPage,
      totalPages,
      onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
      onPrev: () => setPage(p => Math.max(1, p - 1)),
      onNext: () => setPage(p => Math.min(totalPages, p + 1)),
    },
  };
}
